import { useEffect, useMemo, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { api, parseErrorDetail } from "../lib/api";

// Fields displayed on AP view — the R23 whitelist:
//   Product, Backorder Qty, Delivered Qty, Remaining Qty,
//   Delivery Date, Xero Invoice Ref.
// NO price, GST, payment, Reward, supplier, delay reason, ETA — the API
// response schema is asserted to only ever carry these fields.

function LinesTable({ lines }) {
  if (!lines?.length) {
    return <p className="apwo-muted">No backordered lines.</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="apwo-table apwo-cat-table" data-testid="ap-backorder-lines">
        <thead>
          <tr>
            <th>Product</th>
            <th className="apwo-th-right">Backorder Qty</th>
            <th className="apwo-th-right">Delivered Qty</th>
            <th className="apwo-th-right">Remaining Qty</th>
            <th>Latest Delivery Date</th>
            <th>Latest Xero Ref</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.product_id} data-testid={`ap-bo-row-${l.product_id}`}>
              <td>
                <div>{l.product_display_name}</div>
                <div className="apwo-cell-muted">{l.product_id}</div>
              </td>
              <td className="apwo-th-right">{l.backorder_qty}</td>
              <td className="apwo-th-right">{l.delivered_qty}</td>
              <td className="apwo-th-right">
                <strong data-testid={`ap-bo-remaining-${l.product_id}`}>{l.remaining_qty}</strong>
              </td>
              <td>{l.latest_delivery_date || "—"}</td>
              <td>{l.latest_xero_ref || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeliveriesTable({ deliveries }) {
  if (!deliveries?.length) {
    return <p className="apwo-muted">No delivery records yet.</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="apwo-table apwo-cat-table" data-testid="ap-backorder-history">
        <thead>
          <tr>
            <th>Delivery Date</th>
            <th>Xero Invoice Ref</th>
            <th>Product</th>
            <th className="apwo-th-right">Delivered Qty</th>
            <th className="apwo-th-right">Remaining After</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {deliveries.flatMap((d) =>
            d.items.map((it, i) => (
              <tr key={`${d.delivery_id}-${it.product_id}-${i}`}
                  data-testid={`ap-bo-delivery-${d.delivery_id}-${it.product_id}`}
                  className={d.state !== "ACTIVE" ? "apwo-oos-row" : ""}>
                {i === 0 ? (
                  <>
                    <td rowSpan={d.items.length}>{d.delivery_date}</td>
                    <td rowSpan={d.items.length}><code>{d.xero_ref}</code></td>
                  </>
                ) : null}
                <td>
                  <div>{it.product_display_name}</div>
                  <div className="apwo-cell-muted">{it.product_id}</div>
                </td>
                <td className="apwo-th-right">{it.delivered_qty}</td>
                <td className="apwo-th-right">{it.remaining_after}</td>
                {i === 0 ? (
                  <td rowSpan={d.items.length}>
                    <span className={`apwo-cat-status-pill ${d.state === "ACTIVE" ? "apwo-cat-status-pill--in" : "apwo-cat-status-pill--out"}`}>
                      {d.state === "ACTIVE" ? "Recorded" : d.state}
                    </span>
                  </td>
                ) : null}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function MyBackorders() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/backorders/mine")
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(parseErrorDetail(e)); });
    return () => { alive = false; };
  }, []);

  const currentTotalRemaining = useMemo(() => {
    return (data?.current?.lines || []).reduce((sum, l) => sum + l.remaining_qty, 0);
  }, [data]);

  return (
    <PortalLayout title="Backorder Status">
      <section className="apwo-panel" data-testid="ap-backorder-shell">
        <header className="apwo-catalogue-toolbar">
          <div>
            <h2 style={{ margin: 0 }}>Legacy Backorder Status</h2>
            <p className="apwo-muted" style={{ marginTop: 4 }}>
              Read-only view of quantities we owe you from orders placed before
              this portal. This does not include current portal orders or any
              pricing/invoice information — only quantities and delivery
              references.
            </p>
          </div>
        </header>

        {err && <div className="apwo-alert-error" role="alert" data-testid="ap-bo-error">{err}</div>}
        {!data && !err && <p className="apwo-muted">Loading…</p>}

        {data && !data.has_backorder && (
          <div className="apwo-alert-success" role="status" data-testid="ap-bo-empty">
            You have no legacy backorders on record.
          </div>
        )}

        {data?.current && (
          <div data-testid="ap-bo-current">
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0 12px" }}>
              <h3 style={{ margin: 0 }}>Open</h3>
              <span className="apwo-cat-status-pill apwo-cat-status-pill--out" data-testid="ap-bo-total-remaining">
                Total remaining: {currentTotalRemaining}
              </span>
            </div>
            <LinesTable lines={data.current.lines} />
            <h3 style={{ marginTop: 20 }}>Delivery history</h3>
            <DeliveriesTable deliveries={data.current.deliveries} />
          </div>
        )}

        {data?.history?.length > 0 && (
          <div data-testid="ap-bo-completed">
            <h3 style={{ marginTop: 24 }}>Completed history</h3>
            {data.history.map((h) => (
              <div key={h.id} style={{ marginBottom: 20 }}>
                <p className="apwo-muted">Completed {h.completed_at?.slice(0,10) || "—"}</p>
                <LinesTable lines={h.lines} />
                <DeliveriesTable deliveries={h.deliveries} />
              </div>
            ))}
          </div>
        )}
      </section>
    </PortalLayout>
  );
}
